import { useEffect, useState } from 'react';
import { GANHE_MAIS_CORRETOR_CLIENT_URL } from '../../constants/ClientUrls';
import { isMobile } from 'react-device-detect';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './style.module.css';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/forms/Input';
import { HelmetComponent } from '../../components/Helmet';
import { Loading } from '../../components/Loading';
import { Notify, NotifyUpdate } from '../../components/Notify';
import { ModalBrConsorcio } from './components/ModalBrConsorcio';
import { RegisterFormGanheMaisCorretor } from './components/RegisterFormGanheMaisCorretor';
import { RegisterFormInfluencer } from './components/RegisterFormInfluencer';
import * as routes from '../../constants/Routes';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../server/api';
import { maskTEL } from '../../utils/mask';
import { ModalUserExists } from './components/ModalUserExists';
import { getSearchParams } from '../../utils/getSearchParams';
import { FloatingTutorials } from '../../components/FloatingTutorials';
import { decodeSafedata } from '../../utils/decodeSafeData';

interface FormValues {
  name: string;
  email: string;
  phoneNumber?: string;
  password: string;
  confirmPassword?: string;
  regional?: string;
  acceptedTerms?: boolean;
}

export function Register() {
  const navigate = useNavigate();
  const location = useLocation();

  const { getClientByClientIdAndPlayerId, client, loadingClient, signIn } =
    useAuth();
  const [modalIsOpenBrConsorcio, setModalIsOpenBrConsorcio] =
    useState<boolean>(false);
  const [modalIsOpenUserExists, setModalIsOpenUserExists] =
    useState<boolean>(false);
  const [messageUserExists, setMessageUserExists] = useState<string>('');
  const [isNewDataRegistration, setIsNewDataRegistration] =
    useState<boolean>(false);
  const [isQuickRegister, setIsQuickRegister] = useState(false);

  const isIframeRegisterRoute = location.pathname.includes('/iframe-register');

  // Deixa só o card de cadastro visível dentro do iframe: sem isso, o
  // background do <body>/<html> (opaco, definido globalmente) pinta atrás
  // do iframe inteiro, escondendo a página do cliente por trás.
  useEffect(() => {
    if (!isIframeRegisterRoute) return;
    const previousBodyBackground = document.body.style.backgroundColor;
    const previousHtmlBackground =
      document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    return () => {
      document.body.style.backgroundColor = previousBodyBackground;
      document.documentElement.style.backgroundColor = previousHtmlBackground;
    };
  }, [isIframeRegisterRoute]);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();

  useEffect(() => {
    const isQuickRegister = getSearchParams('quickRegister');
    const isBusiness = getSearchParams('isBusiness');
    const clientId = getSearchParams('client');
    const playerId = getSearchParams('player');

    if (playerId && clientId)
      getClientByClientIdAndPlayerId(String(clientId), String(playerId));

    if (isQuickRegister) {
      setIsQuickRegister(true);
    }

    if (isBusiness) {
      localStorage.setItem('isBusiness', isBusiness);
    }
  }, []);

  useEffect(() => {
    const quickName = getSearchParams('name');
    const quickEmail = getSearchParams('email');
    const quickPassowrd = getSearchParams('password');
    const quickPhone = getSearchParams('phone');

    if (isQuickRegister && client) {
      quickRegisterCreate({
        email: decodeURIComponent(quickEmail.toString()),
        name: decodeURIComponent(quickName.toString()),
        password: atob(quickPassowrd.toString()),
        confirmPassword: atob(quickPassowrd.toString()),
        phoneNumber: quickPhone ? atob(quickPhone.toString()) : '',
      });
    }
  }, [isQuickRegister, client]);

  const toggleModalBrConsorcio = () => {
    setModalIsOpenBrConsorcio(!modalIsOpenBrConsorcio);
  };

  const toggleModalUserExists = () => {
    setModalIsOpenUserExists(!modalIsOpenUserExists);
  };

  function maskEmail(email: string) {
    // Separar o e-mail em duas partes: o nome de usuário e o domínio
    const [username, domain] = email.split('@');

    // Pegar o primeiro e o último caractere do nome de usuário
    const firstChar = username[0];
    const lastChar = username[username.length - 1];

    // Gerar os asteriscos, com a quantidade dependendo do tamanho do nome de usuário
    const maskedPart = '*'.repeat(username.length - 2);

    // Combinar o primeiro caractere, os asteriscos e o último caractere com o domínio
    return `${firstChar}${maskedPart}${lastChar}@${domain}`;
  }

  const quickRegisterCreate = async (formData: FormValues) => {
    let actionMgm: any = {};

    const action = getSearchParams('action');
    const clientId = getSearchParams('client');
    const playerId = getSearchParams('player');

    if (action && clientId && playerId) {
      actionMgm = {
        playerId: playerId,
        actionId: action,
        clientId: clientId,
      };
    }

    const nId = Notify('Criando sua conta...', 'loading');

    try {
      const parameters = {
        actionMgm,
        actionId: actionMgm?.actionId,
        player: {
          ...formData,
          clientId: clientId,
          hostId: actionMgm && actionMgm.playerId ? actionMgm.playerId : null,
          viaIframe: isIframeRegisterRoute,
        },
        client: client,
        targetPublic: client.targetPublic,
        page: 'register',
      };

      const { data } = await api.post('/createPlayer', parameters);
      //verificação para determinar se deu erro na requisição de criação
      if (data.data.status) {
        if (data.data.code === 'auth/invalid-password') {
          setMessageUserExists('Senha em formato inválido');
          setIsNewDataRegistration(false);
        } else if (data.data.status === 'error/user-exists-other-client') {
          const newMessageDefaultUserExists = `Identificamos que o email ${maskEmail(getValues('email'))} já existe nesta Plataforma. Para continuar você pode escolher umas dessas opções abaixo.`;
          setMessageUserExists(newMessageDefaultUserExists);
          setIsNewDataRegistration(false);
        }

        toggleModalUserExists();
        return;
      }

      if (client && client.mgmVinculated && client.mgmVinculated === 'true') {
        sessionStorage.removeItem('clientId');
        sessionStorage.removeItem('playerId');
        sessionStorage.removeItem('action');
      }

      await signIn({
        email: parameters.player.email,
        password: parameters.player.password,
        clientId: `${parameters.player.clientId}`,
      });

      if (client && client.clientUrl === 'bomnegocio') {
        navigate(routes.REGISTER_DETAILS);
      } else {
        navigate(routes.HOME);
      }
    } catch (error) {
      console.log('handleForm error', error);

      NotifyUpdate(
        'Tivemos um problema para validar os dados cadastrados, tente novamente!',
        'error',
        nId
      );
    }
  };

  const handleForm = handleSubmit(async (formData) => {
    let actionMgm: any = {};

    if (formData.password !== formData.confirmPassword) {
      return Notify('Confirme as senhas', 'warn');
    }

    if (
      sessionStorage.getItem('action') &&
      sessionStorage.getItem('clientId') &&
      sessionStorage.getItem('playerId')
    ) {
      const action = sessionStorage.getItem('action');
      const clientId = sessionStorage.getItem('clientId');
      const playerId = sessionStorage.getItem('playerId');
      actionMgm = {
        playerId: playerId?.replace(/["]/g, ''),
        actionId: action?.replace(/["]/g, ''),
        clientId: clientId?.replace(/["]/g, ''),
      };
    }

    if (
      getSearchParams('action') &&
      getSearchParams('client') &&
      getSearchParams('player')
    ) {
      const action = getSearchParams('action');
      const clientId = getSearchParams('client');
      const playerId = getSearchParams('player');
      actionMgm = {
        playerId: playerId,
        actionId: action,
        clientId: clientId,
      };
    }

    const nId = Notify('Criando sua conta...', 'loading');

    try {
      const parameters = {
        actionMgm,
        actionId: actionMgm?.actionId,
        player: {
          ...formData,
          clientId: client.id,
          hostId: actionMgm && actionMgm.playerId ? actionMgm.playerId : null,
          viaIframe: isIframeRegisterRoute,
        },
        targetPublic: client.targetPublic,
        client: client,
        page: 'register',
      };

      const { data } = await api.post('/createPlayer', parameters);
      //verificação para determinar se deu erro na requisição de criação
      if (data.data.status) {
        if (data.data.code === 'auth/invalid-password') {
          setMessageUserExists('Senha em formato inválido');
          setIsNewDataRegistration(false);
        } else if (data.data.status === 'error/user-exists-other-client') {
          const newMessageDefaultUserExists = `Identificamos que o email ${maskEmail(getValues('email'))} já existe nesta Plataforma. Para continuar você pode escolher umas dessas opções abaixo.`;
          setMessageUserExists(newMessageDefaultUserExists);
          setIsNewDataRegistration(false);
        }

        toggleModalUserExists();
        return;
      }

      if (client && client.mgmVinculated && client.mgmVinculated === 'true') {
        sessionStorage.removeItem('clientId');
        sessionStorage.removeItem('playerId');
        sessionStorage.removeItem('action');
      }

      await signIn({
        email: parameters.player.email,
        password: parameters.player.password,
        clientId: parameters.player.clientId,
      });

      if (client && client.clientUrl === 'bomnegocio') {
        navigate(routes.REGISTER_DETAILS);
      }
      if (isIframeRegisterRoute) {
        // O registro acontece dentro do iframe embedado no site do cliente.
        // Navegadores particionam o localStorage de conteúdo de terceiros
        // (Safari sempre, Chrome/Firefox em progressão), então o que o
        // signIn() acabou de gravar aqui dentro do iframe NÃO é visível pra
        // aba nova que abrimos abaixo (ela carrega app.oqdigital.com como
        // site de primeiro nível, outro "compartimento" de storage).
        // Por isso passamos o token pela própria URL: a aba nova usa esses
        // parâmetros pra re-autenticar sozinha (ver AuthContext) e depois
        // limpa a URL.
        let accessToken = '';
        let refreshToken = '';
        try {
          const rawPlayerAuth = localStorage.getItem('@pwa:playerAuth');
          if (rawPlayerAuth) {
            const decoded = decodeSafedata(rawPlayerAuth);
            const parsedPlayerAuth = JSON.parse(JSON.parse(decoded));
            accessToken = parsedPlayerAuth?.stsTokenManager?.accessToken || '';
            refreshToken =
              parsedPlayerAuth?.stsTokenManager?.refreshToken || '';
          }
        } catch (error) {
          console.log('Error reading playerAuth for iframe handoff: ', error);
        }

        const homeUrl =
          `${window.location.origin}${routes.HOME}?fromIframeRegister=true` +
          `&iat=${encodeURIComponent(accessToken)}` +
          `&irt=${encodeURIComponent(refreshToken)}` +
          `&iclient=${encodeURIComponent(client.id)}` +
          `&ihost=${encodeURIComponent(actionMgm?.playerId || '')}`;

        // Abre nova aba SEM noopener para manter contexto de armazenamento
        const newWindow = window.open(homeUrl, '_blank');

        if (!newWindow) {
          // Fallback se popup foi bloqueado
          Notify('Por favor, permita pop-ups e tente novamente', 'warn');
        }
      } else {
        navigate(routes.HOME);
      }
    } catch (error) {
      console.log('handleForm error', error);

      NotifyUpdate(
        'Tivemos um problema para validar os dados cadastrados, tente novamente!',
        'error',
        nId
      );
    }
  });

  const onHandleLogin = () => {
    navigate(routes.START);
  };

  if (loadingClient) {
    return <Loading />;
  }

  const isTeamCompetition =
    client && client.isTeamCompetition && client.isTeamCompetition === 'true'
      ? true
      : false;

  if (isTeamCompetition) {
    return <RegisterFormInfluencer />;
  }

  const renderFormBrConsorcio = () => {
    return (
      <div>
        <div className="row">
          {client && client.clientId === 'x7G0bb0NEZUEjXRhMCnKQIc18nM2' && (
            <>
              <p className="text-center">
                <b>Bem-vindo ao Indique e Ganhe. </b>{' '}
              </p>
              <span className="text-center">
                Para cadastro e acesso use o seu e-mail de cliente Prestes.
              </span>
              <br />
              <br />
            </>
          )}
        </div>
        <div className="mb-3">
          <Input
            type="text"
            placeholder="Nome"
            {...register('name', {
              required: true,
            })}
            errors={errors.name ? 'Campo obrigatório' : ''}
          />
        </div>
        <div className="mb-3">
          <Input
            type="text"
            placeholder="E-mail"
            {...register('email', {
              required: true,
            })}
            errors={errors.email ? 'Campo obrigatório' : ''}
          />
        </div>
        <div className="mb-3">
          <Input
            type="password"
            placeholder="Senha"
            {...register('password', {
              required: true,
            })}
            errors={errors.password ? 'Campo obrigatório' : ''}
          />
        </div>
        <div className="mb-3">
          <Input
            type="password"
            placeholder="Confirmar senha"
            {...register('confirmPassword', {
              required: true,
            })}
            errors={errors.confirmPassword ? 'Campo obrigatório' : ''}
          />
        </div>
        <div className="d-grid">
          <Button
            onClick={toggleModalBrConsorcio}
            icon="check"
            text={isSubmitting ? 'Cadastrando' : 'Cadastrar'}
            type="submit"
            color="primary"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      </div>
    );
  };

  const onHandlePageTerms = () => {
    navigate(`${routes.TERMS}`, {
      state: { isCameFromActionPage: true, clientId: client?.id },
    });
  };

  const sendNewDataRegister = async (formData: any) => {
    let actionMgm: any = {};

    if (formData.email !== formData.confirmEmail) {
      return Notify('Confirme os emails', 'warn');
    }
    if (formData.password !== formData.confirmPassword) {
      return Notify('Confirme as senhas', 'warn');
    }

    if (
      sessionStorage.getItem('action') &&
      sessionStorage.getItem('clientId') &&
      sessionStorage.getItem('playerId')
    ) {
      const action = sessionStorage.getItem('action');
      const clientId = sessionStorage.getItem('clientId');
      const playerId = sessionStorage.getItem('playerId');
      actionMgm = {
        playerId: playerId?.replace(/["]/g, ''),
        actionId: action?.replace(/["]/g, ''),
        clientId: clientId?.replace(/["]/g, ''),
      };
    }

    if (
      getSearchParams('action') &&
      getSearchParams('client') &&
      getSearchParams('player')
    ) {
      const action = getSearchParams('action');
      const clientId = getSearchParams('client');
      const playerId = getSearchParams('player');
      actionMgm = {
        playerId: playerId,
        actionId: action,
        clientId: clientId,
      };
    }

    const nId = Notify('Criando sua conta...', 'loading');
    try {
      const parameters = {
        actionMgm,
        actionId: actionMgm?.actionId,
        player: {
          ...formData,
          clientId: client.id,
          hostId: actionMgm && actionMgm.playerId ? actionMgm.playerId : null,
          viaIframe: isIframeRegisterRoute,
        },
        targetPublic: client.targetPublic,
        client: client,
        page: 'register',
      };

      const { data } = await api.post('/createPlayer', parameters);
      //verificação para determinar se deu erro na requisição de criação
      if (data.data.status) {
        if (data.data.status === 'error/user-exists-other-client') {
          const newMessageDefaultUserExists = `Identificamos que o email ${maskEmail(getValues('email'))} já existe nesta Plataforma. Para continuar você pode escolher umas dessas opções abaixo.`;
          setMessageUserExists(newMessageDefaultUserExists);
          setIsNewDataRegistration(false);
        }
        toggleModalUserExists();
        return;
      }

      if (client && client.mgmVinculated && client.mgmVinculated === 'true') {
        sessionStorage.removeItem('clientId');
        sessionStorage.removeItem('playerId');
        sessionStorage.removeItem('action');
      }

      await signIn({
        email: parameters.player.email,
        password: parameters.player.password,
        clientId: parameters.player.clientId,
      });

      navigate(routes.HOME);
    } catch (error) {
      console.log('handleForm error', error);

      NotifyUpdate(
        'Tivemos um problema para validar os dados cadastrados, tente novamente!',
        'error',
        nId
      );
    }
  };

  const generateNewEmail = () => {
    const formValues = getValues();
    let newEmail: string | Array<string> = formValues.email
      .toString()
      .split('@');
    newEmail = `${newEmail[0]}+${client.clientUrl ? client.clientUrl : Date.now()}@${
      newEmail[1]
    }`;
    setValue('email', newEmail);
    toggleModalUserExists();
    sendNewDataRegister(getValues());
  };

  const sendPasswordResetEmail = async () => {
    try {
      const hostname = window.location.host;
      const parameters = {
        searchFunctionality: 'sendPasswordResetEmail',
        userType: 'player',
        clientId: client.id,
        playerEmail: getValues('email'),
        hostname,
      };

      await api.post('/search', parameters);
      Notify(
        `Encaminhamos para o email ${getValues('email')} as instruções para recuperação da senha`,
        'success'
      );
    } catch (error) {
      console.log('sendPasswordResetEmail error', error);
    }
  };

  return (
    <>
      <HelmetComponent
        clientLogoURL={client?.logoURL}
        companyName={client?.companyName}
        clientFavicon={client?.favIcon}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={'Cadastro'}
        googleAnalyticsIntegration={
          client?.marketingIntegration?.integration === 'googleAnalytics' &&
          client?.marketingIntegration.code
        }
        pixelIntegration={
          client?.marketingIntegration?.integration === 'facebookPixel' &&
          client?.marketingIntegration.code
        }
      />
      <div
        style={{
          background:
            isMobile || isIframeRegisterRoute
              ? ''
              : `url(${client && client.loginImage})`,
          backgroundColor: isIframeRegisterRoute ? 'transparent' : undefined,
        }}
        className={`${styles.contentFull} ${styles.backgroundImage}`}
      >
        <div className={styles.contentLeft}>
          {isMobile && !isIframeRegisterRoute && (
            <img className="img-fluid" src={client?.loginImageMobile} />
          )}
        </div>
        <div className={styles.contentRight}>
          <div className={styles.centeredAllFlex}>
            <div className="m-3">
              <Card isLarge shadow>
                {isQuickRegister && (
                  <>
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="me-3">Cadastro rápido! Aguarde...</span>
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  </>
                )}
                {isIframeRegisterRoute &&
                (getSearchParams('client') === '6a0ef6f536e1c939dc5ca4cc' ||
                  client?.clientUrl === GANHE_MAIS_CORRETOR_CLIENT_URL) ? (
                  <RegisterFormGanheMaisCorretor />
                ) : client && client.clientUrl === 'brconsorcio' ? (
                  renderFormBrConsorcio()
                ) : (
                  <>
                    {!isQuickRegister && (
                      <form onSubmit={handleForm}>
                        <div className="row">
                          {client &&
                            client.clientId ===
                              'x7G0bb0NEZUEjXRhMCnKQIc18nM2' && (
                              <>
                                <p className="text-center">
                                  <b>Bem-vindo ao Indique e Ganhe. </b>{' '}
                                </p>
                                <span className="text-center">
                                  Para cadastro e acesso use o seu e-mail de
                                  cliente Prestes.
                                </span>
                                <br />
                                <br />
                              </>
                            )}
                        </div>
                        <div className="mb-2">
                          <Input
                            type="text"
                            placeholder="Nome"
                            {...register('name', {
                              required: true,
                            })}
                            errors={errors.name ? 'Campo obrigatório' : ''}
                          />
                        </div>
                        <div className="mb-2">
                          <Input
                            type="email"
                            placeholder="E-mail"
                            {...register('email', {
                              required: true,
                            })}
                            errors={errors.email ? 'Campo obrigatório' : ''}
                          />
                        </div>
                        <div className="mb-2">
                          <Input
                            type="text"
                            placeholder="Telefone"
                            {...register('phoneNumber', {
                              required: true,
                              onChange(event) {
                                const { value } = event.target;
                                event.target.value = maskTEL(value);
                              },
                            })}
                            maxLength={15}
                            errors={
                              errors.phoneNumber ? 'Campo obrigatório' : ''
                            }
                          />
                        </div>
                        <div className="mb-2">
                          <Input
                            type="password"
                            placeholder="Senha"
                            {...register('password', {
                              required: true,
                            })}
                            errors={errors.password ? 'Campo obrigatório' : ''}
                          />
                        </div>
                        <div className="mb-2">
                          <Input
                            type="password"
                            placeholder="Confirmar senha"
                            {...register('confirmPassword', {
                              required: true,
                            })}
                            errors={
                              errors.confirmPassword ? 'Campo obrigatório' : ''
                            }
                          />
                        </div>
                        <div className="mb-2">
                          <div className="d-flex flex-row-reverse me-check">
                            <div className="mb-1">
                              <span className="tiny-text">
                                Ao clicar em{' '}
                                <span className={styles.register}>
                                  Cadastrar
                                </span>{' '}
                                , você concorda com as{' '}
                                <a
                                  className="fw-bold"
                                  target="_blank"
                                  href="https://firebasestorage.googleapis.com/v0/b/oqhomapi.appspot.com/o/TermsAndPolitics%2FPOLI%CC%81TICA%20DE%20PRIVACIDADE%20LGPD%20COMPLIANCE%202026.pdf?alt=media&token=f6339049-9c75-4ac2-8417-a70c9d75be94"
                                >
                                  Política de Privacidade
                                </a>{' '}
                                e{' '}
                                <a
                                  className="fw-bold clickable"
                                  onClick={onHandlePageTerms}
                                >
                                  Termos de uso{' '}
                                </a>
                                do programa.
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="d-grid">
                          <Button
                            icon="check"
                            text={isSubmitting ? 'Cadastrando' : 'Cadastrar'}
                            type="submit"
                            color="primary"
                            isLoading={isSubmitting}
                            disabled={isSubmitting}
                          />
                        </div>

                        {!getSearchParams('action') &&
                          !getSearchParams('client') &&
                          !getSearchParams('player') && (
                            <div className="mt-3">
                              <div className="d-grid">
                                <Button
                                  text="Fazer login"
                                  icon="login"
                                  onClick={onHandleLogin}
                                />
                              </div>
                            </div>
                          )}
                      </form>
                    )}
                  </>
                )}
              </Card>
            </div>
          </div>
        </div>
        {!isIframeRegisterRoute && (
          <FloatingTutorials
            title="Ajuda"
            subtitle="Veja o(s) vídeo(s) e tire suas dúvidas:"
            links={[
              {
                title: 'Como se cadastrar',
                link: 'https://drive.google.com/file/d/1ErOj2-10WdhxI7R5DJOpin1-hJKWeGoh/preview',
              },
            ]}
          />
        )}
        <ModalBrConsorcio
          modalIsOpen={modalIsOpenBrConsorcio}
          toggleModal={toggleModalBrConsorcio}
          handleForm={handleForm}
        />
        <ModalUserExists
          modalIsOpen={modalIsOpenUserExists}
          generateNewEmail={generateNewEmail}
          restorePassword={sendPasswordResetEmail}
          newDataRegistration={isNewDataRegistration}
          toggleModal={toggleModalUserExists}
          message={messageUserExists}
        />
      </div>
    </>
  );
}
